import React, { useState } from "react";
import { ActionPlansEditionContext, initialState } from "./ActionPlansEditionContext";
import {
    IActionPlansEditionContext,
    IActionPlansEditionContextProps,
    IActionPlanEdition,
} from "./ActionPlansEditionContextInterface";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";

export const ActionPlansEditionProvider: React.FC<
    IActionPlansEditionContextProps & {
        onAfterSave?: () => Promise<void> | void;
        onError?: (error: Error) => void;
    }
> = ({ children, onAfterSave, onError }) => {
    const [edit, setEdit] = useState<IActionPlanEdition[]>(initialState.edit);
    const [isSavingInline, setIsSavingInline] = useState<boolean>(initialState.isSavingInline);
    const { updateActionPlanMutation } = ActionPlanEntity.UpdateActionPlan();

    const handleStartInlineEdit = (row: IActionPlan) => {
        setEdit([
            {
                id: row.action_plan_id,
                changes: {
                    name: row.name || "",
                    status_id: row.status?.status_id,
                    due_date: row.due_date ? new Date(row.due_date).toISOString() : undefined,
                    assignee_id: row.assignee?.user_id,
                },
            },
        ]);
    };

    const handleCancelInlineEdit = (rowId: number) => {
        setEdit([]);
    };

    const handleSaveInlineEdit = async (row: IActionPlan) => {
        const existingEdition = edit.findIndex((e) => e.id === row.action_plan_id);
        if (existingEdition === -1) {
            return;
        }

        const editedName = edit[existingEdition]?.changes?.name;
        const editedStatusId = edit[existingEdition]?.changes?.status_id;
        const editedDueDate = edit[existingEdition]?.changes?.due_date;
        const editedAssigneeId = edit[existingEdition]?.changes?.assignee_id;

        if (editedName !== undefined && editedName.trim().length === 0) {
            handleCancelInlineEdit(row.action_plan_id);
            return;
        }

        const hasNameChange = editedName !== undefined && editedName.trim() !== row.name?.trim();
        const hasStatusChange = editedStatusId !== undefined && editedStatusId !== row.status?.status_id;
        const rowDueDateISO = row.due_date ? new Date(row.due_date).toISOString() : undefined;
        const hasDueDateChange = editedDueDate !== undefined && editedDueDate !== rowDueDateISO;
        const hasAssigneeChange = editedAssigneeId !== undefined && editedAssigneeId !== row.assignee_id;

        if (!hasNameChange && !hasStatusChange && !hasDueDateChange && !hasAssigneeChange) {
            handleCancelInlineEdit(row.action_plan_id);
            return;
        }

        setIsSavingInline(true);

        try {
            const changes: { name?: string; status_id?: number; due_date?: string; assignee_id?: number } = {};
            if (hasNameChange) {
                changes.name = editedName?.trim();
            }
            if (hasStatusChange) {
                changes.status_id = editedStatusId;
            }
            if (hasDueDateChange) {
                changes.due_date = editedDueDate;
            }
            if (hasAssigneeChange) {
                changes.assignee_id = editedAssigneeId;
            }

            await updateActionPlanMutation({
                variables: {
                    actionPlanId: row.action_plan_id,
                    changes,
                },
            });

            if (onAfterSave) {
                await onAfterSave();
            }
        } catch (error) {
            if (onError) {
                onError(error instanceof Error ? error : new Error("An error occurred while updating the action plan"));
            }
        } finally {
            setIsSavingInline(false);
            handleCancelInlineEdit(row.action_plan_id);
        }
    };

    const baseValue: IActionPlansEditionContext = {
        edit,
        setEdit,
        isSavingInline,
        setIsSavingInline,
        handleStartInlineEdit,
        handleSaveInlineEdit,
        handleCancelInlineEdit,
    };

    return <ActionPlansEditionContext.Provider value={baseValue}>{children}</ActionPlansEditionContext.Provider>;
};
